import { useCallback, useEffect, useState } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { toast } from 'sonner'
import { FileText, Loader2, Upload } from 'lucide-react'

import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import { Button } from '@/components/ui/button'

/**
 * CPA-side attachment of source documents to a deal.
 *
 * Optional by design: the seller portal remains the normal route in. This is
 * for the common case where the CPA already holds the statements — emailed,
 * or handed over at the kickoff call — and would otherwise have to wait for
 * the seller to re-upload files that are already on their desk.
 *
 * Files go to the same store as seller uploads, so the extraction pass picks
 * them up without caring who uploaded them. The audit trail records the CPA.
 */

// Matches MAX_UPLOAD_BYTES in apps/api/routers/qoe/documents.py.
const MAX_BYTES = 25 * 1024 * 1024
const MAX_MB = MAX_BYTES / (1024 * 1024)

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
}

// VALIDATION-1C: the same words the server uses, so a file rejected here and
// the same file rejected there read identically. Which side caught it is an
// implementation detail the CPA should never be able to infer.
const UNSUPPORTED_TYPE = "That file type isn't supported. Upload a PDF, Excel, or CSV."
const EMPTY_FILE = 'This file is empty — nothing to read.'

const tooLarge = (bytes: number) =>
  `This file is ${(bytes / (1024 * 1024)).toFixed(1)} MB — the limit is ${MAX_MB} MB. ` +
  'Upload a smaller export or split it.'

const FALLBACK_CATEGORIES = [
  { key: 'profit_loss', label: 'Profit & loss statement' },
  { key: 'balance_sheet', label: 'Balance sheet' },
  { key: 'tax_returns', label: 'Tax returns' },
  { key: 'bank_statements', label: 'Bank statements' },
  { key: 'gl_trial_balance', label: 'General ledger / trial balance' },
  { key: 'other', label: 'Other supporting documents' },
]

interface CatalogEntry { key: string; label: string }

interface AttachedDoc {
  doc_id: string
  category: string
  filename: string
  size: number
  stored: boolean
  extraction_status: string | null
  uploaded_at: string | null
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Reject a file whose bytes are not what its extension claims. Checked here as
 * well as server-side so the CPA is told which file is bad before waiting for
 * a 25 MB upload to finish.
 */
async function looksReadable(file: File): Promise<boolean> {
  if (file.name.toLowerCase().endsWith('.csv')) return true
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer())
  if (head.length < 4) return false
  const pdf = head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46
  const zip = head[0] === 0x50 && head[1] === 0x4b && head[2] === 0x03 && head[3] === 0x04
  return pdf || zip
}

export function SourceDocumentUploader({ dealId }: { dealId: string }) {
  const [open, setOpen] = useState(false)
  const [categories, setCategories] = useState<CatalogEntry[]>(FALLBACK_CATEGORIES)
  const [category, setCategory] = useState('profit_loss')
  const [uploading, setUploading] = useState(false)
  const [attached, setAttached] = useState<AttachedDoc[]>([])

  const loadAttached = useCallback(async () => {
    try {
      const res = await apiClient.get<{ files: AttachedDoc[] }>(
        `/qoe/deals/${encodeURIComponent(dealId)}/documents`,
      )
      setAttached(res.data.files ?? [])
    } catch {
      // The list is a convenience — a failure here must not block uploading.
    }
  }, [dealId])

  useEffect(() => { loadAttached() }, [loadAttached])

  useEffect(() => {
    let cancelled = false
    async function loadCategories() {
      try {
        const res = await apiClient.get<{ catalog?: CatalogEntry[] }>(
          `/qoe/deals/${encodeURIComponent(dealId)}/document-requests`,
        )
        const cat = res.data.catalog
        if (!cancelled && Array.isArray(cat) && cat.length) {
          setCategories(cat.map(c => ({ key: c.key, label: c.label })))
        }
      } catch {
        // Keep the fallback list — the categories it names are all real.
      }
    }
    loadCategories()
    return () => { cancelled = true }
  }, [dealId])

  const onDrop = useCallback(
    async (accepted: File[]) => {
      if (!accepted.length) return
      setUploading(true)
      let uploaded = 0
      try {
        for (const file of accepted) {
          if (file.size === 0) {
            toast.error(file.name, { description: EMPTY_FILE })
            continue
          }
          if (file.size > MAX_BYTES) {
            toast.error(file.name, { description: tooLarge(file.size) })
            continue
          }
          if (!(await looksReadable(file))) {
            toast.error(file.name, { description: UNSUPPORTED_TYPE })
            continue
          }
          const form = new FormData()
          form.append('file', file)
          try {
            await apiClient.post(
              `/qoe/deals/${encodeURIComponent(dealId)}/documents` +
                `?category=${encodeURIComponent(category)}`,
              form,
              { headers: { 'Content-Type': 'multipart/form-data' } },
            )
            uploaded += 1
          } catch (err) {
            // formatApiError now surfaces the router's written `detail` rather
            // than "413 - HTTP 413" (see utils/httpErrors.ts), so a server-side
            // rejection reads the same as the client-side one above.
            toast.error(`${file.name} was not attached`, { description: formatApiError(err) })
          }
        }
      } finally {
        setUploading(false)
      }
      if (uploaded) {
        toast.success(
          uploaded === 1 ? 'Document attached' : `${uploaded} documents attached`,
        )
        loadAttached()
      }
    },
    [dealId, category, loadAttached],
  )

  /**
   * VALIDATION-1C: react-dropzone filters on `accept` and `maxSize` before
   * onDrop ever runs, and rejected files went nowhere — drop a .docx and the
   * dropzone simply blinked. Silence reads as "the app is broken", and the CPA
   * retries the same file. Say why, per file.
   */
  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    for (const { file, errors } of rejections) {
      const codes = errors.map(e => e.code)
      const description = codes.includes('file-too-large')
        ? tooLarge(file.size)
        : codes.includes('file-too-small') // maxSize's counterpart: a 0-byte file
          ? EMPTY_FILE
          : UNSUPPORTED_TYPE
      toast.error(file.name, { description })
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: ACCEPT,
    maxSize: MAX_BYTES,
    disabled: uploading,
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {attached.length === 0
            ? 'No source documents on this deal yet.'
            : `${attached.length} source document${attached.length === 1 ? '' : 's'} attached.`}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <Upload className="mr-2 h-4 w-4" />
          {open ? 'Close' : 'Attach source documents'}
        </Button>
      </div>

      {attached.length > 0 && (
        <ul className="space-y-1">
          {attached.slice(0, 8).map(d => (
            <li key={d.doc_id} className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{d.filename}</span>
              <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                {fmtBytes(d.size)}
              </span>
              {!d.stored && (
                <span className="shrink-0 text-xs text-amber-700">
                  file not in storage
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="space-y-2 rounded-md border p-3">
          <label className="block text-xs font-medium" htmlFor="src-doc-category">
            File this document as
          </label>
          <select
            id="src-doc-category"
            value={category}
            onChange={e => setCategory(e.target.value)}
            disabled={uploading}
            className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
          >
            {categories.map(c => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>

          <div
            {...getRootProps()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-6 text-center text-sm ${
              isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
            } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading…
              </span>
            ) : (
              <>
                <Upload className="mb-2 h-5 w-5 text-muted-foreground" />
                <span>Drop files here, or click to choose</span>
                <span className="mt-1 text-xs text-muted-foreground">
                  PDF, Excel, or CSV · up to 25 MB each
                </span>
              </>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Optional. The seller can still upload these through their own portal
            link — anything you attach here appears alongside their files and is
            read by the same extraction pass.
          </p>
        </div>
      )}
    </div>
  )
}
